import { Controller } from "@hotwired/stimulus"

const SPINNER_SVG = `<svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
</svg>`

export default class extends Controller {
  static values = { id: String }

  showDeleteModal(event) {
    const name = event.currentTarget.dataset.autoboostName || "this autoboost"
    const url = event.currentTarget.dataset.autoboostUrl

    const template = document.getElementById("delete-modal-template")
    if (!template) return

    const clone = template.content.cloneNode(true)
    const nameEl = clone.querySelector(".delete-name")
    if (nameEl) nameEl.textContent = name

    const form = clone.querySelector(".delete-form")
    if (form) {
      form.action = url
      const submitBtn = form.querySelector('button[type="submit"]')
      if (submitBtn) {
        submitBtn.addEventListener("click", (e) => {
          e.preventDefault()
          this.setButtonLoading(submitBtn, "Deleting...")
          this.element.classList.add("opacity-50", "pointer-events-none")
          form.requestSubmit()
        })
      }
      form.addEventListener("turbo:submit-end", () => {
        const modal = form.closest("[data-controller='modal']")
        if (modal) modal.remove()
      })
    }

    document.body.appendChild(clone)
  }

  startLoading(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const form = btn.closest("form")
    this.setButtonLoading(btn)
    if (form) {
      form.requestSubmit()
    }
  }

  async boost(event) {
    // Read raw string attributes to avoid Stimulus Number coercion
    // Meta IDs exceed Number.MAX_SAFE_INTEGER and lose precision as JS Numbers
    const postId = event.currentTarget.getAttribute("data-autoboost-card-post-id-param")
    const autoboostId = event.currentTarget.getAttribute("data-autoboost-card-autoboost-id-param")
    const paused = event.currentTarget.getAttribute("data-autoboost-card-paused-param") === "true"
    const btn = event.currentTarget

    // AB-074: a paused autoboost can still boost a chosen post (supported
    // manual workflow) — but never silently. Confirm first.
    if (paused) {
      console.log("[autoboost-card] boost on paused autoboost — asking for confirmation")
      this.confirmPausedBoost(btn, postId, autoboostId)
      return
    }

    await this.performBoost(btn, postId, autoboostId)
  }

  // AB-074: JS-built confirm modal (same pattern as wizard-footer's reason
  // modal — no template dependency, app modal styling, no native confirm).
  confirmPausedBoost(btn, postId, autoboostId) {
    document.getElementById("paused-boost-confirm-modal")?.remove()

    const modal = document.createElement("div")
    modal.id = "paused-boost-confirm-modal"
    modal.className = "fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
    modal.innerHTML = `
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
        <div class="flex items-center gap-3 mb-4">
          <div class="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-full">
            <svg class="w-6 h-6 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">This autoboost is paused</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">New posts aren't boosted automatically</p>
          </div>
        </div>
        <p class="text-gray-600 dark:text-gray-300 mb-6">
          Do you want to boost this post anyway? The ad will go live even though the autoboost stays paused.
        </p>
        <div class="flex justify-end gap-3">
          <button type="button" data-paused-boost-cancel
                  class="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer">
            Cancel
          </button>
          <button type="button" data-paused-boost-confirm
                  class="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors cursor-pointer">
            Boost anyway
          </button>
        </div>
      </div>
    `

    document.body.appendChild(modal)

    const dismiss = () => modal.remove()
    modal.addEventListener("click", (e) => { if (e.target === modal) dismiss() })
    modal.querySelector("[data-paused-boost-cancel]").addEventListener("click", dismiss)
    modal.querySelector("[data-paused-boost-confirm]").addEventListener("click", () => {
      console.log("[autoboost-card] paused boost confirmed")
      dismiss()
      this.performBoost(btn, postId, autoboostId)
    })
  }

  async performBoost(btn, postId, autoboostId) {
    btn.disabled = true
    btn.innerHTML = SPINNER_SVG + '<span>Boosting...</span>'
    btn.classList.add("inline-flex", "items-center", "gap-2")

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(`/api/gp/v1/autoboosts/${autoboostId}/trigger`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ fb_post_id: postId })
      })

      const data = await res.json()

      if (res.ok && data.payload?.verified) {
        const adsCount = data.payload.ads_count || 0
        // AB-008/AB-013: Show "Processing" until Meta API confirms. The ad creation
        // happens async — AdCreationJob will set the final status.
        btn.innerHTML = `<svg class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg><span>Processing</span>`
        btn.classList.remove("bg-blue-600", "hover:bg-blue-700", "cursor-pointer")
        btn.classList.add("bg-amber-600", "cursor-default", "inline-flex", "items-center", "gap-2")
        btn.disabled = true

        if (adsCount > 0) {
          const badge = document.querySelector('[data-tab="ads"] .rounded-full')
          if (badge) {
            const current = parseInt(badge.textContent.trim(), 10) || 0
            badge.textContent = current + adsCount
          }
        }
      } else {
        const msg = data?.error?.error_message || "Failed"
        btn.innerHTML = msg
        btn.classList.remove("bg-blue-600", "hover:bg-blue-700")
        btn.classList.add("bg-red-500", "text-white")
        setTimeout(() => {
          btn.innerHTML = `<svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/></svg><span>Boost</span>`
          btn.classList.remove("bg-red-500", "text-white")
          btn.classList.add("bg-blue-600", "hover:bg-blue-700", "cursor-pointer")
          btn.disabled = false
        }, 3000)
      }
    } catch (e) {
      btn.innerHTML = "Error"
      btn.disabled = false
      console.error("[autoboost-card] Boost error:", e)
    }
  }

  async destroyAction(event) {
    const actionId = event.currentTarget.getAttribute("data-autoboost-card-action-id-param")
    const autoboostId = event.currentTarget.getAttribute("data-autoboost-card-autoboost-id-param")
    const btn = event.currentTarget
    const row = btn.closest("tr")

    btn.disabled = true
    btn.innerHTML = SPINNER_SVG + '<span>Deleting...</span>'
    btn.classList.add("inline-flex", "items-center", "gap-2", "opacity-50", "pointer-events-none")

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(`/api/gp/v1/autoboosts/${autoboostId}/actions/${actionId}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        }
      })

      if (res.ok) {
        const nextRow = row.nextElementSibling
        if (nextRow && nextRow.querySelector("td[colspan]")) nextRow.remove()
        row.remove()

        const badge = document.querySelector('[data-tab="debug"] .rounded-full')
        if (badge) {
          const current = parseInt(badge.textContent.trim(), 10) || 0
          if (current > 0) badge.textContent = current - 1
        }
      } else {
        const data = await res.json().catch(() => ({}))
        btn.innerHTML = data?.error?.error_message || "Failed"
        btn.disabled = false
        btn.classList.remove("opacity-50", "pointer-events-none")
      }
    } catch (e) {
      btn.innerHTML = "Error"
      btn.disabled = false
      btn.classList.remove("opacity-50", "pointer-events-none")
      console.error("[autoboost-card] Destroy action error:", e)
    }
  }

  // --- Individual Ad Controls ---

  // AB-052: pause is now FULLY async on the backend (enqueues
  // AutoboostAction(ad_pause); the worker performs the Meta API call). The
  // frontend immediately puts the button in a "Pausing..." state and stays
  // there until the SSE broadcast updates the ad row to :paused (or the user
  // navigates away). No more `window.location.reload()` — the broadcast
  // subscription wired to the autoboost channel handles the inline update.
  async pauseAd(event) {
    const adId = event.currentTarget.getAttribute("data-autoboost-card-ad-id-param")
    const autoboostId = event.currentTarget.getAttribute("data-autoboost-card-autoboost-id-param")
    const btn = event.currentTarget
    console.log(`[autoboost-card] pauseAd: queueing async pause for ad=${adId}`)
    this.setButtonLoading(btn, "Pausing...")
    try {
      const res = await this._adApi(autoboostId, adId, "pause")
      if (res.ok) {
        // Backend has queued AutoboostAction(ad_pause). Leave button in
        // "Pausing..." state. The SSE broadcast will replace the ad row
        // when the worker completes (or fails) the pause.
        console.log(`[autoboost-card] pause queued for ad=${adId} — awaiting broadcast`)
      } else {
        await this._handleAdError(btn, res)
      }
    } catch (e) {
      this._showError(btn, "Error")
      console.error("[autoboost-card] Pause error:", e)
    }
  }

  async startAd(event) {
    const adId = event.currentTarget.getAttribute("data-autoboost-card-ad-id-param")
    const autoboostId = event.currentTarget.getAttribute("data-autoboost-card-autoboost-id-param")
    const btn = event.currentTarget
    this.setButtonLoading(btn, "Starting...")
    try {
      const res = await this._adApi(autoboostId, adId, "start")
      if (res.ok) {
        window.location.reload()
      } else {
        await this._handleAdError(btn, res)
      }
    } catch (e) {
      this._showError(btn, "Error")
      console.error("[autoboost-card] Start error:", e)
    }
  }

  async processAd(event) {
    const adId = event.currentTarget.getAttribute("data-autoboost-card-ad-id-param")
    const autoboostId = event.currentTarget.getAttribute("data-autoboost-card-autoboost-id-param")
    const btn = event.currentTarget
    this.setButtonLoading(btn, "Processing...")
    try {
      const res = await this._adApi(autoboostId, adId, "start")
      if (res.ok) {
        window.location.reload()
      } else {
        await this._handleAdError(btn, res)
      }
    } catch (e) {
      this._showError(btn, "Error")
      console.error("[autoboost-card] Process error:", e)
    }
  }

  // AB-065: retry is async. Queue it, leave the button in "Retrying…", and let
  // the Mercure ping (FbAd#notify_ads_changed → ads_poller frame reload) replace
  // the row with its final state when AdCreationJob completes. NO
  // window.location.reload() — that fired before the async job finished, so the
  // reload showed the ad still failing and forced a manual refresh. Same
  // broadcast-driven pattern as pauseAd.
  async retryAd(event) {
    const adId = event.currentTarget.getAttribute("data-autoboost-card-ad-id-param")
    const autoboostId = event.currentTarget.getAttribute("data-autoboost-card-autoboost-id-param")
    const btn = event.currentTarget
    console.log(`[autoboost-card] retryAd: queueing async retry for ad=${adId} — awaiting broadcast`)
    this.setButtonLoading(btn, "Retrying...")
    try {
      const res = await this._adApi(autoboostId, adId, "start")
      if (!res.ok) {
        await this._handleAdError(btn, res)
      }
      // ok: leave button loading; ads_poller reloads the row on the ping.
    } catch (e) {
      this._showError(btn, "Error")
      console.error("[autoboost-card] Retry error:", e)
    }
  }

  // An ad that exists on Meta (row has data-ad-controllable="true") is live or
  // paused there, so deleting it asks first. Failed and still-queued ads
  // delete in one click, as before.
  async deleteAd(event) {
    const adId = event.currentTarget.getAttribute("data-autoboost-card-ad-id-param")
    const autoboostId = event.currentTarget.getAttribute("data-autoboost-card-autoboost-id-param")
    const btn = event.currentTarget
    const row = btn.closest("[data-ad-row]")

    if (row?.getAttribute("data-ad-controllable") === "true") {
      console.log(`[autoboost-card] deleteAd: ad=${adId} exists on Meta — asking for confirmation`)
      this.confirmDeleteAd(btn, adId, autoboostId)
      return
    }

    await this.performDeleteAd(btn, adId, autoboostId)
  }

  // Same JS-built modal pattern as confirmPausedBoost (no native confirm).
  confirmDeleteAd(btn, adId, autoboostId) {
    document.getElementById("delete-ad-confirm-modal")?.remove()

    const modal = document.createElement("div")
    modal.id = "delete-ad-confirm-modal"
    modal.className = "fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
    modal.innerHTML = `
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
        <div class="flex items-center gap-3 mb-4">
          <div class="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
            <svg class="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
            </svg>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Delete this ad?</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">This can't be undone</p>
          </div>
        </div>
        <p class="text-gray-600 dark:text-gray-300 mb-6">
          The ad will be removed from Meta.
        </p>
        <div class="flex justify-end gap-3">
          <button type="button" data-delete-ad-cancel
                  class="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer">
            Cancel
          </button>
          <button type="button" data-delete-ad-confirm
                  class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors cursor-pointer">
            Delete ad
          </button>
        </div>
      </div>
    `

    document.body.appendChild(modal)

    const dismiss = () => modal.remove()
    modal.addEventListener("click", (e) => { if (e.target === modal) dismiss() })
    modal.querySelector("[data-delete-ad-cancel]").addEventListener("click", dismiss)
    modal.querySelector("[data-delete-ad-confirm]").addEventListener("click", () => {
      console.log(`[autoboost-card] delete confirmed for ad=${adId}`)
      dismiss()
      this.performDeleteAd(btn, adId, autoboostId)
    })
  }

  async performDeleteAd(btn, adId, autoboostId) {
    this.setButtonLoading(btn, "Deleting...")
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(`/api/gp/v1/autoboosts/${autoboostId}/ads/${adId}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" }
      })
      if (res.ok) {
        window.location.reload()
      } else {
        await this._handleAdError(btn, res)
      }
    } catch (e) {
      this._showError(btn, "Error")
      console.error("[autoboost-card] Delete ad error:", e)
    }
  }

  // --- Group Controls ---

  async pauseGroup(event) {
    await this._groupAction(event, "pause", "[data-ad-current-status='active'][data-ad-controllable='true']", "Pausing All...")
  }

  async startGroup(event) {
    await this._groupAction(event, "start", "[data-ad-current-status='paused'][data-ad-controllable='true']", "Starting All...")
  }

  async processGroup(event) {
    await this._groupAction(event, "start", "[data-ad-processable='true']", "Processing...")
  }

  // data-ad-needs-attention marks failed ads AND queued ads carrying an error —
  // rate-limited ads stay pending, and they need the retry too.
  async retryGroup(event) {
    // AB-065: broadcast-driven — leave buttons loading; ads_poller reloads the
    // frame when the async retries complete. No premature page reload.
    await this._groupAction(event, "start", "[data-ad-needs-attention='true']", "Retrying All...", false)
  }

  // Ads tab toolbar "Retry all failures": the group retry across every group.
  async retryAllFailures(event) {
    await this._groupAction(event, "start", "[data-ad-needs-attention='true']", "Retrying...", false, this.element)
  }

  async deleteGroup(event) {
    const autoboostId = event.currentTarget.getAttribute("data-autoboost-card-autoboost-id-param")
    const btn = event.currentTarget
    const card = btn.closest("[data-group-id]")
    const groupId = card?.dataset.groupId
    this.setButtonLoading(btn, "Deleting...")

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      await fetch(`/api/gp/v1/autoboosts/${autoboostId}/ad_groups/${groupId}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" }
      })
    } catch (e) {
      console.error("[autoboost-card] Delete group error:", e)
    }
    window.location.reload()
  }

  // --- Private Helpers ---

  // `scope` defaults to the clicked button's group card.
  async _groupAction(event, action, selector, loadingText, reloadOnDone = true, scope = null) {
    const autoboostId = event.currentTarget.getAttribute("data-autoboost-card-autoboost-id-param")
    const btn = event.currentTarget
    const container = scope || btn.closest("[data-group-id]")
    this.setButtonLoading(btn, loadingText)

    const rows = container.querySelectorAll(`[data-ad-row]${selector}`)
    const adIds = Array.from(rows).map(r => r.getAttribute("data-ad-api-id"))

    if (adIds.length === 0) {
      if (reloadOnDone) window.location.reload()
      return
    }

    try {
      await Promise.all(adIds.map(id => this._adApi(autoboostId, id, action)))
    } catch (e) {
      console.error(`[autoboost-card] Group ${action} error:`, e)
    }
    // AB-065: reloadOnDone=false for retry — the async creates haven't
    // finished yet; the Mercure ping reloads the frame when they do.
    if (reloadOnDone) window.location.reload()
  }

  async _adApi(autoboostId, adId, action) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    return fetch(`/api/gp/v1/autoboosts/${autoboostId}/ads/${adId}/${action}`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" }
    })
  }

  async _handleAdError(btn, res) {
    const data = await res.json().catch(() => ({}))
    this._showError(btn, data?.error?.error_message || "Failed")
  }

  // Icon-only buttons (data-icon-only) keep their square size: the message
  // moves into the tooltip and the original icon comes back.
  _showError(btn, msg) {
    btn.classList.remove("opacity-50", "pointer-events-none")
    if (btn.hasAttribute("data-icon-only") && btn.dataset.originalHtml) {
      btn.innerHTML = btn.dataset.originalHtml
      btn.title = msg
      return
    }
    btn.innerHTML = msg
  }

  setButtonLoading(btn, text) {
    btn.classList.add("opacity-50", "pointer-events-none")
    if (btn.hasAttribute("data-icon-only")) {
      if (!btn.dataset.originalHtml) btn.dataset.originalHtml = btn.innerHTML
      btn.innerHTML = SPINNER_SVG
      return
    }
    if (text) {
      btn.innerHTML = SPINNER_SVG + `<span>${text}</span>`
    } else {
      const svg = btn.querySelector("svg")
      if (svg) svg.outerHTML = SPINNER_SVG
    }
    btn.classList.add("inline-flex", "items-center", "gap-2")
  }
}
